<script setup lang="ts">
/**
 * MY — Beneficial Owner Notices (Section 60C, M32108) 문서-뷰 패널.
 * 설문으로 통지 선택 → 해당 문서만 Stage 패키지 + 공유 values + person/address lookup.
 */
import { ref, reactive, computed, provide, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail, SignerEntry } from '@/features/AI-documents/types/docGen.types';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import {
  BO_NOTICE_DOCS, BO_NOTICE_DOC_CODES, labelize, typeOf, isOptional,
  adaptFormDataForDoc,
  type BoNoticeDocCode,
} from './boNotices/boNoticeFields';
import { resolveIncludedDocs, normalizeSurvey, type BoNoticeSurvey } from './boNotices/boNoticeSurvey';
import { BO_NOTICE_DOC_CTX } from './boNotices/boNoticeContext';
import { buildBoNoticeVariables, emptyBoNoticeState, type BoNoticeState } from '../mappers/boNoticesMapper';
import { parseMalaysianAddress, isEmptyMyAddress, formatMyAddressLine, composeMyAddressLine } from '@/shared/utils/malaysianAddress';
import { fetchEntityAddresses } from '@/shared/composables/useEntityAddresses';
import { docGenApi } from '@/features/AI-documents/api/docGenApi';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import AddressInputModal from './cdd/AddressInputModal.vue';
import BoNoticesPackageDoc from './boNotices/BoNoticesPackageDoc.vue';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: { fieldGroup: string; index: number } | null;
  editTaskId?: number | null;
  studio?: boolean;
  appliedAddress?: { key: string; line: string; town: string; state: string; postcode: string; country: string; seq: number } | null;
  pendingPick?: { value: string; seq: number } | null;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const entityCd = computed(() => String(props.entityData?.entityCd || props.entityData?.icpCd || '').trim());

const taskTitleLocal = ref('');
const titleModel = computed({
  get: () => taskTitleLocal.value || props.taskTitle || '',
  set: (v: string) => { taskTitleLocal.value = v; },
});
const effectiveTitle = computed(() => titleModel.value.trim());

const state = reactive<BoNoticeState>(emptyBoNoticeState());

const activeKey = ref('');
const inputKey = ref('');
const inputText = ref('');
const dateMenu = ref(false);

function setTargetSigner(
  docCode: BoNoticeDocCode,
  person: { name: string; address?: string } | null,
  title: string,
  designationKey: string,
  force = false,
) {
  if (!person?.name) {
    if (force) {
      state.signers[docCode] = [];
      state.values[designationKey] = '';
    }
    return;
  }
  const cur = String(state.signers[docCode]?.[0]?.name || '').trim();
  if (!force && cur && cur === person.name) {
    if (!String(state.values[designationKey] || '').trim()) state.values[designationKey] = title;
    return;
  }
  if (!force && cur && cur !== person.name) {
    // 수신자가 바뀌었으면 싸인대상도 교체
  } else if (!force && cur) {
    return;
  }

  state.signers[docCode] = [{
    id: `tgt-${docCode}-${person.name}`,
    name: person.name,
    title,
    role: 'recipient',
    showTitle: true,
    _signerLayout: 'box',
  }];
  state.values[designationKey] = title;
}

/** 수신란 사람 → 해당 문서 [[Signers]](싸인대상) 동기화 (수동 지정 시 included 여부와 무관하게 매핑) */
function syncRecipientToSigners(fieldKey: string, force = true) {
  const name = String(state.values[fieldKey] || '').trim();
  if (!name) return;
  const person = { name };

  if (fieldKey === 'corporate_member_name') {
    setTargetSigner('BO01', person, 'Corporate Member', 'signer_designation_BO01', force);
  }
  if (fieldKey === 'member_name') {
    setTargetSigner('BO02', person, 'Individual Member', 'signer_designation_BO02', force);
  }
  if (fieldKey === 'info_holder_name') {
    setTargetSigner('BO04', person, 'Information Holder', 'signer_designation_BO04', force);
  }
  if (fieldKey === 'bo_name') {
    setTargetSigner('BO03', person, 'Beneficial Owner', 'signer_designation_BO03', force);
    setTargetSigner('BO05', person, 'Beneficial Owner', 'signer_designation_BO05', force);
    setTargetSigner('BO06', person, 'Beneficial Owner', 'signer_designation_BO06', force);
  }
}

function setFieldValue(key: string, val: string) {
  state.values[key] = val;
  if (/^(corporate_member_name|member_name|bo_name|info_holder_name)$/.test(key)) {
    syncRecipientToSigners(key, true);
  }
}

function activate(key: string) {
  activeKey.value = key;
  const t = typeOf(key);
  if (t === 'person' || t === 'entity') {
    const defaultRole = (key === 'bo_name') ? 'BENEFICIAL_OWNER'
      : (key === 'member_name' || key === 'corporate_member_name') ? 'SHAREHOLDER'
        : null;
    emit('request-person-lookup', {
      fieldGroup: 'bo-notices',
      key,
      defaultRole,
      lookupType: key === 'company_name' || t === 'entity' ? 'entity' : 'officer',
    });
    inputKey.value = '';
  } else {
    inputKey.value = key;
    inputText.value = state.values[key] || '';
    if (t === 'date') dateMenu.value = true;
  }
}
function onPickDate(d: any) {
  const date = Array.isArray(d) ? d[0] : d;
  if (!date) return;
  const dt = new Date(date);
  inputText.value = Number.isNaN(dt.getTime())
    ? String(date)
    : `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
  dateMenu.value = false;
  commitInput();
}
function commitInput() {
  if (inputKey.value) setFieldValue(inputKey.value, inputText.value.trim());
  inputKey.value = ''; activeKey.value = '';
}
function skipInput() { inputKey.value = ''; activeKey.value = ''; }

function flatAddr(v: any): string {
  return formatMyAddressLine(String(v ?? ''));
}

const pendingPickValue = ref('');
watch(() => props.pendingPick, (p) => { pendingPickValue.value = p?.value || ''; });
function placePick(key: string): boolean {
  if (!pendingPickValue.value) return false;
  setFieldValue(key, pendingPickValue.value);
  pendingPickValue.value = '';
  return true;
}

const addrModal = ref(false);
const addrKey = ref('');
const addrLabel = ref('');
const addrInitial = ref<{ line?: string }>({});
function openAddress(key: string) {
  if (props.studio) { addrKey.value = key; emit('request-address-input', key); return; }
  addrKey.value = key;
  addrLabel.value = labelize(key);
  addrInitial.value = { line: state.values[key] || '' };
  addrModal.value = true;
}
function applyAddressTo(key: string, a: { line: string; town: string; state: string; postcode: string; country: string }) {
  if (!key) return;
  state.values[key] = composeMyAddressLine(a);
}
function applyAddress(a: { line: string; town: string; state: string; postcode: string; country: string }) {
  applyAddressTo(addrKey.value, a);
}
watch(() => props.appliedAddress, (a) => { if (a?.key) applyAddressTo(a.key, a); });

function signersOf(varKey: string): SignerEntry[] { return state.signers[varKey] || []; }
function setSigners(varKey: string, list: SignerEntry[]) {
  state.signers[varKey] = Array.isArray(list) ? list : [];
}

function applyPersonToField(key: string, a: any) {
  const name = a.name || a.contactName || [a.firstName, a.middleName, a.surname].filter(Boolean).join(' ') || '';
  state.values[key] = name;

  // Bonus/Allotment 과 동일: addresses[] 에서 Residential(직책 MAP) 우선, 없으면 primary/residential
  const addrs = Array.isArray(a.addresses) ? a.addresses : [];
  const addrFromArr = (x: any) => flatAddr(x?.composed || x?.value || x?.address || '');
  const addrKind = (x: any) => [
    x?.type, x?.addressType, x?.addressTypeName, x?.label, x?.originalLabel,
  ].map((s) => String(s || '')).join(' ');
  const isResidential = (x: any) => /residential|registered|home/i.test(addrKind(x));

  const fullAddr = a.residentialAddress || a.primaryAddress || '';
  const res = a.myResidential && !isEmptyMyAddress(a.myResidential) ? a.myResidential : parseMalaysianAddress(fullAddr);
  const fromParsed = !isEmptyMyAddress(res)
    ? composeMyAddressLine(res)
    : '';
  const addrLine = fromParsed
    || flatAddr(fullAddr)
    || addrFromArr(addrs.find(isResidential))
    || addrFromArr(addrs[0])
    || flatAddr(a.address || '');

  if (addrLine) {
    // 사람 재지정 시 주소도 같이 교체 (보너스와 동일)
    if (key === 'member_name') state.values.member_address = addrLine;
    if (key === 'corporate_member_name') state.values.corporate_member_address = addrLine;
    if (key === 'bo_name') state.values.bo_address = addrLine;
    if (key === 'info_holder_name') state.values.info_holder_address = addrLine;
  }

  // 수신란에 지정한 사람 = 해당 문서 싸인대상
  syncRecipientToSigners(key, true);
}
watch(() => props.selectedPerson, (p) => {
  const ctx = props.activePersonContext;
  if (!p || !ctx || ctx.fieldGroup !== 'bo-notices' || !ctx.key) return;
  applyPersonToField(ctx.key, p as any);
});

provide(BO_NOTICE_DOC_CTX, {
  valueOf: (k) => state.values[k] || '',
  labelOf: labelize,
  typeOf,
  activeKey,
  activate,
  setFieldValue,
  openAddress,
  entityCd,
  signersOf,
  setSigners,
  pendingPickValue,
  placePick,
});

function todayDDMMYYYY() {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

const entityPrefilled = ref('');
/** entity 멤버 후보 — Corporate(B09001) / Individual(B09002) 분리. 유형 없으면 수신란 비움. */
const pendingCorporateMember = ref<{ name: string; address: string } | null>(null);
const pendingIndividualMember = ref<{ name: string; address: string } | null>(null);
const pendingBo = ref<{ name: string; address: string } | null>(null);

function isCorporateMember(m: any): boolean {
  const cat = String(m?.categoryCd || m?.CATEGORY_CD || '').trim().toUpperCase();
  if (cat === 'B09001') return true;
  if (cat === 'B09002') return false;
  // 보조 신호
  if (m?.corporateName || m?.myShareholderKind === 'BodyCorporate') return true;
  return false;
}

function memberPick(m: any): { name: string; address: string } {
  const name = String(m.name || m.contactName || m.CONTACT_NAME || '').trim();
  const addrs = Array.isArray(m.addresses) ? m.addresses : [];
  const addrKind = (x: any) => [
    x?.type, x?.addressType, x?.addressTypeName, x?.label, x?.originalLabel,
  ].map((s) => String(s || '')).join(' ');
  const residential = addrs.find((x: any) => /residential|registered|home/i.test(addrKind(x)));
  const addr = flatAddr(
    residential?.composed || residential?.value || residential?.address
    || m.primaryAddress || m.residentialAddress
    || addrs[0]?.composed || addrs[0]?.value || addrs[0]?.address
    || '',
  );
  return { name, address: addr };
}

watch(entityCd, async (cd) => {
  if (!cd || cd === entityPrefilled.value) return;
  const isEntitySwitch = !!entityPrefilled.value && entityPrefilled.value !== cd;
  entityPrefilled.value = cd;
  const put = (k: string, v: any, force = false) => {
    const s = String(v ?? '').trim();
    if (!s) return;
    if (force || !String(state.values[k] || '').trim()) state.values[k] = s;
  };
  const force = isEntitySwitch;

  put('company_name', props.entityData?.companyName || props.entityData?.entityName, force);
  put('company_registration_no', props.entityData?.registrationNo, force);
  put('notice_date', todayDDMMYYYY(), force);

  try {
    const gov = await fetchEntityAddresses(cd);
    if (gov) {
      put('company_name', gov.companyName, force);
      put('company_registration_no', gov.regNo, force);
      put('registered_office_address', flatAddr(gov.registered), force);
    }
  } catch (e) { console.warn('[BO Notices] entity address fetch failed', e); }

  // 문서별 싸인대상(=수신자) 후보 — applyRecipientPrefill 에서 반영
  try {
    const mres: any = await docGenApi.getMembers(cd);
    const members: any[] = mres?.data?.members || mres?.members || [];
    const corp = members.find((m) => isCorporateMember(m));
    const indiv = members.find((m) => !isCorporateMember(m));
    pendingCorporateMember.value = corp ? memberPick(corp) : null;
    pendingIndividualMember.value = indiv ? memberPick(indiv) : null;
  } catch (e) {
    console.warn('[BO Notices] getMembers failed', e);
    pendingCorporateMember.value = null;
    pendingIndividualMember.value = null;
  }

  // BO 후보 (없으면 개인주주를 의심 BO 후보로 — 60C(2) 싸인대상)
  try {
    const boRes: any = await docGenApi.getBeneficialOwners(cd);
    const bos: any[] = boRes?.data?.beneficialOwners || boRes?.data?.beneficial_owners
      || boRes?.data?.list || boRes?.beneficialOwners || (Array.isArray(boRes?.data) ? boRes.data : []);
    if (Array.isArray(bos) && bos.length > 0) {
      const b = bos[0];
      const name = String(b.name || b.contactName || b.fullName || b.BO_NAME || '').trim();
      const cc = String(b.contactCd || b.CONTACT_CD || '').trim();
      let addr = flatAddr(b.address || b.residentialAddress || b.primaryAddress || '');
      // 보너스와 동일: entity+직책 MAP 주소 (addressScope=officer)
      if (cc) {
        try {
          const boPos = String(b.positionCd || b.POSITION_CD || 'B30046').trim();
          const detRes: any = await docGenApi.getContactDetail(cd, cc, {
            addressScope: 'officer',
            positionCd: boPos,
          });
          const det = detRes?.data?.data || detRes?.data || detRes || {};
          const addrs = Array.isArray(det.addresses) ? det.addresses : [];
          const kind = (x: any) => [x?.type, x?.label, x?.originalLabel].map((s) => String(s || '')).join(' ');
          const res = addrs.find((x: any) => /residential|registered|home/i.test(kind(x)));
          addr = flatAddr(
            res?.composed || res?.value
            || det.residentialAddress || det.primaryAddress
            || addrs[0]?.composed || addrs[0]?.value
            || addr,
          );
        } catch (e) {
          console.warn('[BO Notices] BO address enrich failed', e);
        }
      }
      // BO 주소 없으면 동일 이름 개인주주 주소 보강
      if (!addr && pendingIndividualMember.value && pendingIndividualMember.value.name === name) {
        addr = pendingIndividualMember.value.address;
      }
      pendingBo.value = { name, address: addr };
    } else {
      pendingBo.value = null;
    }
  } catch (e) {
    console.warn('[BO Notices] getBeneficialOwners failed', e);
    pendingBo.value = null;
  }

  // Change entity — 멤버/BO/싸인어도 새 회사 후보로 강제 재바인딩
  if (isEntitySwitch) {
    state.values.info_holder_name = '';
    state.values.info_holder_address = '';
    state.signers.BO04 = [];
    state.values.signer_designation_BO04 = '';
    if (state.survey?.completed) applyRecipientPrefill(true);
  } else if (state.survey?.completed) {
    applyRecipientPrefill(false);
  }
}, { immediate: true });

const includedDocCodes = computed(() => resolveIncludedDocs(state.survey));

function applyRecipientPrefill(force = false) {
  const codes = includedDocCodes.value;
  const put = (k: string, v: string) => {
    if (!v) return;
    if (force || !String(state.values[k] || '').trim()) state.values[k] = v;
  };
  const clearBoRecipient = () => {
    state.values.bo_name = '';
    state.values.bo_address = '';
    for (const code of ['BO03', 'BO05', 'BO06'] as BoNoticeDocCode[]) {
      state.signers[code] = [];
      state.values[`signer_designation_${code}`] = '';
    }
  };

  // BO01 — 법인주주만 (없으면 비움)
  if (codes.includes('BO01')) {
    if (pendingCorporateMember.value?.name) {
      put('corporate_member_name', pendingCorporateMember.value.name);
      put('corporate_member_address', pendingCorporateMember.value.address);
      setTargetSigner('BO01', pendingCorporateMember.value, 'Corporate Member', 'signer_designation_BO01', force);
    } else if (force) {
      state.values.corporate_member_name = '';
      state.values.corporate_member_address = '';
      state.signers.BO01 = [];
      state.values.signer_designation_BO01 = '';
    }
  }

  // BO02 — 개인주주만
  if (codes.includes('BO02')) {
    if (pendingIndividualMember.value?.name) {
      put('member_name', pendingIndividualMember.value.name);
      put('member_address', pendingIndividualMember.value.address);
      setTargetSigner('BO02', pendingIndividualMember.value, 'Individual Member', 'signer_designation_BO02', force);
    } else if (force) {
      state.values.member_name = '';
      state.values.member_address = '';
      state.signers.BO02 = [];
      state.values.signer_designation_BO02 = '';
    }
  }

  // BO03 — 의심 BO (등록 BO 없으면 개인주주를 후보로)
  if (codes.includes('BO03')) {
    const target = pendingBo.value?.name
      ? pendingBo.value
      : (pendingIndividualMember.value || null);
    if (target?.name) {
      put('bo_name', target.name);
      put('bo_address', target.address);
      setTargetSigner('BO03', target, 'Beneficial Owner', 'signer_designation_BO03', force);
    } else if (force) {
      clearBoRecipient();
    }
  }

  // BO05 / BO06 — 등록 BO (없으면 개인주주 fallback 허용: 실무상 동일인인 경우 많음)
  let boBound = false;
  for (const code of ['BO05', 'BO06'] as BoNoticeDocCode[]) {
    if (!codes.includes(code)) continue;
    const target = pendingBo.value?.name
      ? pendingBo.value
      : (pendingIndividualMember.value || null);
    if (target?.name) {
      put('bo_name', target.name);
      put('bo_address', target.address);
      setTargetSigner(code, target, 'Beneficial Owner', `signer_designation_${code}`, force);
      boBound = true;
    } else if (force) {
      state.signers[code] = [];
      state.values[`signer_designation_${code}`] = '';
    }
  }
  if (force && !boBound && !codes.includes('BO03') && !pendingBo.value?.name && !pendingIndividualMember.value?.name) {
    state.values.bo_name = '';
    state.values.bo_address = '';
  }
  // BO04 정보보유자 — 자동 후보 없음 (직접 선택)
}

watch(includedDocCodes, () => {
  if (state.survey?.completed) applyRecipientPrefill(false);
}, { deep: true });

watch(() => state.survey?.completed, (done) => {
  if (done) applyRecipientPrefill(true);
});

// entity 후보가 늦게 도착해도 설문 완료 상태면 다시 채움
watch(
  [pendingCorporateMember, pendingIndividualMember, pendingBo],
  () => {
    if (state.survey?.completed) applyRecipientPrefill(false);
  },
  { deep: true },
);

const overallProgress = computed(() => {
  if (!state.survey?.completed) return 0;
  const codes = includedDocCodes.value.length ? includedDocCodes.value : BO_NOTICE_DOC_CODES;
  const keys = new Set<string>();
  for (const c of codes) {
    BO_NOTICE_DOCS[c].keys.forEach((k) => { if (!isOptional(k)) keys.add(k); });
  }
  const list = [...keys];
  if (!list.length) return 0;
  const filled = list.filter((k) => String(state.values[k] || '').trim()).length;
  return Math.round((filled / list.length) * 100);
});
watch(overallProgress, (v) => emit('progress-change', v), { immediate: true });

function onSurveyUpdate(v: BoNoticeSurvey) {
  state.survey = normalizeSurvey(v);
  state.survey.completed = !!v.completed;
}

const editMode = computed(() => !!props.editTaskId);
const taskConfigSteps = ref<string[]>([]);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });
onMounted(async () => {
  const code = props.selection?.task?.TASK_CODE;
  if (code) {
    try {
      const cfg = await getTaskConfigByCode(code);
      taskConfigSteps.value = (cfg as any)?.processSteps || [];
    } catch { /* noop */ }
  }
});

const availableTemplates = computed(() => (props.selection?.documents || []).filter((d: any) => d.tmpId));

const NAME_KEYWORDS: Record<BoNoticeDocCode, RegExp> = {
  BO01: /60c\s*\(?\s*1\s*\)?.*corporate|corporate.*60c\s*\(?\s*1/i,
  BO02: /60c\s*\(?\s*1\s*\)?.*individual|individual.*60c\s*\(?\s*1/i,
  BO03: /60c\s*\(?\s*2|identified\s*bo/i,
  BO04: /60c\s*\(?\s*3\s*\)?(?!.*5)/i,
  BO05: /60c\s*\(?\s*5.*change|change\s*of\s*particulars/i,
  BO06: /60c\s*\(?\s*5.*confirm|confirm\s*particulars/i,
};
function tmplName(d: any): string {
  return String(d.templateName || d.docNm || d.name || d.fileName || d.orgFileName || d.docCode || '');
}
function templateFileLabel(d: any, fallback: string): string {
  const raw = tmplName(d).trim() || fallback;
  return raw.replace(/\.docx$/i, '').trim() || fallback;
}
function resolveTemplate(docCode: BoNoticeDocCode): { tmpId: number; label: string } | null {
  const docs = availableTemplates.value as any[];
  const byCode = docs.find((d) => String(d.docCode || '').toUpperCase() === docCode);
  if (byCode?.tmpId) {
    return { tmpId: byCode.tmpId, label: templateFileLabel(byCode, BO_NOTICE_DOCS[docCode].label) };
  }
  const re = NAME_KEYWORDS[docCode];
  const byName = docs.find((d) => re.test(tmplName(d)));
  if (byName?.tmpId) {
    return { tmpId: byName.tmpId, label: templateFileLabel(byName, BO_NOTICE_DOCS[docCode].label) };
  }
  return null;
}

async function handleCreateTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) { progress.value = 'Please enter a Task Title.'; return; }
  if (!state.survey?.completed || !includedDocCodes.value.length) {
    progress.value = 'Please complete the notice selection survey first.';
    return;
  }

  const targetCodes = includedDocCodes.value;
  const resolved = targetCodes
    .map((c) => {
      const hit = resolveTemplate(c);
      return hit ? { c, tmpId: hit.tmpId, label: hit.label } : null;
    })
    .filter((r): r is { c: BoNoticeDocCode; tmpId: number; label: string } => !!r);

  if (resolved.length === 0) {
    const avail = (availableTemplates.value as any[]).map((x) => x.templateName || x.docCode).join(', ') || '(none)';
    progress.value = `⚠ 선택한 통지에 연결된 문서 템플릿이 없습니다. 관리자에서 BO Notices(Section 60C) 문서 템플릿을 연결해 주세요. 선택: [${targetCodes.join(', ')}] · 연결됨: [${avail}]`;
    console.warn('[BO Notices] no templates for selected docs', targetCodes, props.selection?.documents);
    return;
  }
  if (resolved.length < targetCodes.length) {
    const missing = targetCodes.filter((c) => !resolved.some((r) => r.c === c));
    console.warn('[BO Notices] some selected docs have no template:', missing);
  }

  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing'),
    };
    const officerData = {
      entityCd: entityCd.value || null,
      presetForm: gatherState(),
      companyName: state.values.company_name || '',
      companyRegistrationNo: state.values.company_registration_no || '',
      noticeDate: state.values.notice_date || '',
      memberName: state.values.member_name || '',
      boName: state.values.bo_name || '',
      survey: JSON.parse(JSON.stringify(state.survey)),
      includedDocs: includedDocCodes.value.slice(),
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id'; creating.value = false; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskData: { formData: officerData } }),
      });
      await fetch(`/api/startTask/tasks/${taskId}/title`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskTitle: effectiveTitle.value }),
      });
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null,
          taskTypeCd: props.selection.task.TASK_CODE,
          taskTypeName: props.selection.task.TASK_NAME,
          taskTitle: effectiveTitle.value, taskFlow, officerData,
        }),
      }).then((r) => r.json());
      taskId = taskRes?.data?.taskId || taskRes?.taskId;
      if (!taskId) {
        progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`;
        creating.value = false; return;
      }
    }

    if (resolved.length) {
      progress.value = 'Preparing documents…';
      const formData = buildBoNoticeVariables(state);
      const docsToSave = resolved.map(({ c, tmpId, label }) => {
        const docSigners = state.signers[c] || [];
        return {
          templateId: tmpId,
          label,
          personName: String(state.values.company_name || 'Company'),
          entityCd: entityCd.value || undefined,
          formData: adaptFormDataForDoc(c, formData),
          signers: docSigners.length ? docSigners : undefined,
        };
      });
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        }).then((r) => r.json());
        if (!saveRes?.success) progress.value = `⚠ Documents: ${saveRes?.message || 'unknown'}`;
      } catch (err: any) {
        console.warn('[BO Notices] document save failed:', err);
      }
    }

    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      });
    } catch { /* ignore */ }
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }
    try {
      const [taskDetailRes, docListRes] = await Promise.all([
        fetch(`/api/startTask/tasks/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
        fetch(`/api/startTask/doc-cust-save/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
      ]);
      sessionStorage.setItem(`presetTaskDetailPrefetch:${taskId}`, JSON.stringify({ taskDetail: taskDetailRes, docList: docListRes }));
    } catch { /* ignore */ }

    emit('task-created', { taskId, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    console.error('[BO Notices] create task 실패', e);
    progress.value = `Error: ${e?.message || e}`;
  } finally {
    creating.value = false;
  }
}

function gatherState() {
  return {
    state: JSON.parse(JSON.stringify(state)),
    survey: JSON.parse(JSON.stringify(state.survey)),
    includedDocs: includedDocCodes.value.slice(),
    companyName: state.values.company_name || '',
    companyRegistrationNo: state.values.company_registration_no || '',
    noticeDate: state.values.notice_date || '',
    memberName: state.values.member_name || '',
    boName: state.values.bo_name || '',
  };
}
function restoreState(s: any) {
  const src = s?.state || s?.presetForm?.state;
  if (src && typeof src === 'object') {
    const base = emptyBoNoticeState();
    Object.assign(state, { ...base, ...src });
    state.values = { ...(src.values || {}) };
    state.signers = { ...(src.signers || {}) };
    state.survey = normalizeSurvey(src.survey || s?.survey || s?.presetForm?.survey || {});
    if (src.survey?.completed || s?.survey?.completed || s?.presetForm?.survey?.completed) {
      state.survey.completed = true;
    }
  } else if (s?.survey || s?.presetForm?.survey) {
    state.survey = normalizeSurvey(s.survey || s.presetForm.survey);
    if ((s.survey || s.presetForm.survey)?.completed) state.survey.completed = true;
  }
}
defineExpose({ gatherState, restoreState, createTask: handleCreateTask });
</script>

<template>
  <div class="otc-panel" :class="{ 'otc-studio': studio }">
    <div v-if="!studio" class="co-header">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME }}</div>
        <div class="co-subtitle">Beneficial Owner Notices (Section 60C)</div>
      </div>
      <v-btn variant="text" size="small" class="text-none" @click="emit('back')"><v-icon start size="16">mdi-arrow-left</v-icon>Back</v-btn>
    </div>

    <div v-if="!studio" class="co-title-row">
      <span class="ct-label">Task Title</span>
      <v-text-field v-model="titleModel" placeholder="e.g. BO Notices — Tilkoblet" variant="outlined" density="compact" hide-details class="ct-input" />
    </div>

    <div v-if="!studio" class="co-entity">
      <v-icon size="18" color="primary">mdi-domain</v-icon>
      <span>{{ entityData?.companyName || entityData?.entityName || 'Entity' }}</span>
      <v-btn variant="tonal" size="x-small" class="text-none ml-auto" @click="emit('change-entity')">Change</v-btn>
    </div>

    <div v-if="inputKey" class="co-inputbar">
      <div class="lbl">{{ labelize(inputKey) }} <span v-if="typeOf(inputKey) === 'date'" class="opt">(optional)</span></div>
      <div class="row">
        <v-menu v-if="typeOf(inputKey) === 'date'" v-model="dateMenu" :close-on-content-click="false">
          <template #activator="{ props: mp }">
            <v-text-field v-bind="mp" v-model="inputText" placeholder="DD/MM/YYYY" variant="outlined" density="compact" hide-details append-inner-icon="mdi-calendar" />
          </template>
          <v-date-picker @update:model-value="onPickDate" />
        </v-menu>
        <v-text-field v-else v-model="inputText" :placeholder="labelize(inputKey)" variant="outlined" density="compact" hide-details autofocus @keydown.enter="commitInput" />
        <v-btn color="primary" size="small" variant="flat" @click="commitInput">Set</v-btn>
        <v-btn v-if="typeOf(inputKey) === 'date'" variant="text" size="small" @click="skipInput">Skip</v-btn>
      </div>
    </div>

    <div class="co-doc-body">
      <BoNoticesPackageDoc
        :studio="!!studio"
        :edit-mode="editMode"
        :creating="creating"
        :survey="state.survey"
        @update:survey="onSurveyUpdate"
        @create-task="handleCreateTask"
      />
    </div>

    <div v-if="!studio || progress" class="co-footer">
      <v-btn v-if="!studio" variant="outlined" class="text-none" @click="emit('back')">Back</v-btn>
      <div class="co-foot-right">
        <span v-if="progress" class="co-progress" :class="{ err: progress.startsWith('Error') || progress.includes('⚠') }">{{ progress }}</span>
        <v-btn
          v-if="!studio"
          color="primary"
          class="text-none"
          :loading="creating"
          :disabled="creating || !state.survey?.completed || !includedDocCodes.length"
          :prepend-icon="editMode ? 'mdi-content-save' : 'mdi-check'"
          @click="handleCreateTask"
        >{{ editMode ? 'Update Task' : 'Create Task' }}</v-btn>
      </div>
    </div>

    <AddressInputModal v-model="addrModal" :label="addrLabel" :has-components="false" :initial="addrInitial" @apply="applyAddress" />
  </div>
</template>

<style scoped>
.otc-panel { display:flex; flex-direction:column; }
.otc-studio { flex:0 0 auto; width:min(1000px, 100%); background:transparent; }
.otc-studio .co-doc-body { background:transparent; padding:0; }
.co-header { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-bottom:1px solid #eef0f6; }
.co-title { font-weight:700; font-size:15px; } .co-subtitle { font-size:12px; color:#8a94a6; }
.co-title-row { display:flex; align-items:center; gap:10px; padding:10px 14px; border-bottom:1px solid #eef0f6; }
.co-title-row .ct-label { font-size:12px; font-weight:700; color:#5b6472; white-space:nowrap; }
.co-title-row .ct-input { max-width:520px; }
.co-entity { display:flex; align-items:center; gap:8px; background:#f0f5ff; padding:8px 14px; font-size:13px; }
.co-inputbar { padding:10px 14px; background:#fafbfe; border-bottom:1px solid #eef0f6; position:sticky; top:0; z-index:5; }
.co-inputbar .lbl { font-size:12px; font-weight:700; color:#1B84FF; margin-bottom:6px; } .co-inputbar .opt { color:#8a94a6; font-weight:400; }
.co-inputbar .row { display:flex; gap:8px; align-items:center; }
.co-doc-body { padding:16px 12px 28px; }
.co-footer { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-top:1px solid #eef0f6; gap:12px; }
.co-foot-right { display:flex; align-items:center; gap:12px; margin-left:auto; }
.co-progress { font-size:12px; color:#5b6472; } .co-progress.err { color:#c0392b; }
</style>
